import type AwcAutocompleteElement from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import { CheckboxGroupState } from 'custom-elements/checkbox/checkbox-group-element';
import CheckboxOptionElement from 'custom-elements/checkbox/checkbox-option-element';
import type RecurringSelectElement from 'custom-elements/recurring-select-element';
import { removeItem } from 'helpers/array';
import dayjs from 'lib/dayjs';
import log from './logger';

enum RuleType {
  Daily = 'IceCube::DailyRule',
  Weekly = 'IceCube::WeeklyRule',
  Monthly = 'IceCube::MonthlyRule',
}

enum RepeatType {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

enum OccurrenceEndType {
  Never = 'never',
  On = 'on',
  After = 'after',
}

interface ValidationHash {
  day?: number[];
  day_of_week?: Record<number, number[]>;
}

export interface RecurringHash {
  count?: number;
  interval: number;
  rule_type: RuleType;
  until?: string;
  validations: ValidationHash;
  week_start?: number;
}

export default class RecurringSelect {
  element: RecurringSelectElement;
  select: AwcAutocompleteElement;
  modal: HTMLElement;
  recurringHash: RecurringHash | Record<string, never> = {};
  daySelectContainer: HTMLElement | null = null;
  monthDaySelectContainer: HTMLElement | null = null;
  monthDaySelect: HTMLSelectElement | null = null;
  monthOrdinalSelect: HTMLSelectElement | null = null;
  repeatField: HTMLInputElement | null = null;
  repeatType: HTMLSelectElement | null = null;
  checkboxOptions: CheckboxOptionElement[] | [] = [];
  endRadios: HTMLInputElement[] | [] = [];
  occurrenceEndAfterField: HTMLInputElement | null = null;
  occurrenceEndOnField: HTMLInputElement | null = null;
  submitBtn: HTMLButtonElement | null = null;

  constructor(
    element: RecurringSelectElement,
    select: AwcAutocompleteElement,
    modal: HTMLElement,
    recurringHash: RecurringHash | null
  ) {
    this.element = element;
    this.select = select;
    this.modal = modal;

    this.daySelectContainer = this.modal.querySelector('[data-day-selector]');
    this.monthDaySelectContainer = this.modal.querySelector('[data-month-day-selector]');
    this.monthDaySelect = this.modal.querySelector('[data-month-day-select]');
    this.monthOrdinalSelect = this.modal.querySelector('[data-month-ordinal-select]');
    this.repeatField = this.modal.querySelector('[data-repeat-field]');
    this.repeatType = this.modal.querySelector('[data-repeat-type]');
    this.checkboxOptions = Array.from(this.modal.querySelectorAll('checkbox-option'));
    this.endRadios = Array.from(this.modal.querySelectorAll('[data-end-time]'));
    this.occurrenceEndAfterField = this.modal.querySelector('[data-occurrence-end-after-field]');
    this.occurrenceEndOnField = this.modal.querySelector('[data-occurrence-end-on-field]');
    this.submitBtn = this.modal.querySelector('[data-recurrence-submit-btn]');

    this.recurringHash = recurringHash || this.defaultRecurringHash;

    this.onRepeatTypeChange = this.onRepeatTypeChange.bind(this);
    this.onRepeatFieldInput = this.onRepeatFieldInput.bind(this);
    this.onOccurrenceEndInput = this.onOccurrenceEndInput.bind(this);
    this.onDayChange = this.onDayChange.bind(this);
    this.onMonthChange = this.onMonthChange.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.onEndChange = this.onEndChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.repeatType?.addEventListener('change', this.onRepeatTypeChange);
    this.repeatField?.addEventListener('input', this.onRepeatFieldInput);
    this.occurrenceEndOnField?.addEventListener('change', this.onDateChange);
    this.occurrenceEndAfterField?.addEventListener('input', this.onOccurrenceEndInput);
    this.modal.addEventListener('checkbox-group:change', (event) => this.onDayChange(event as CustomEvent));
    this.endRadios.forEach((radio) => radio.addEventListener('change', this.onEndChange));
    this.monthDaySelect?.addEventListener('change', this.onMonthChange);
    this.monthOrdinalSelect?.addEventListener('change', this.onMonthChange);
    this.submitBtn?.addEventListener('click', this.onSubmit);
  }

  start() {
    switch (this.recurringHash.rule_type) {
      case RuleType.Daily:
        this.setDailyRule();
        break;
      case RuleType.Weekly:
        this.setWeeklyRule();
        break;
      case RuleType.Monthly:
        this.setMonthlyRule();
        break;
      default:
        log.warn(`Unhandled rule type: ${this.recurringHash.rule_type}`);
        break;
    }
  }

  stop() {
    this.repeatType?.removeEventListener('change', this.onRepeatTypeChange);
    this.repeatField?.removeEventListener('input', this.onRepeatFieldInput);
    this.occurrenceEndOnField?.removeEventListener('change', this.onDateChange);
    this.occurrenceEndAfterField?.removeEventListener('input', this.onOccurrenceEndInput);
    this.modal.removeEventListener('checkbox-group:change', (event) => this.onDayChange(event as CustomEvent));
    this.endRadios.forEach((radio) => radio.removeEventListener('change', this.onEndChange));
    this.monthDaySelect?.removeEventListener('change', this.onMonthChange);
    this.monthOrdinalSelect?.removeEventListener('change', this.onMonthChange);
    this.submitBtn?.removeEventListener('click', this.onSubmit);
  }

  onRepeatTypeChange(event: Event) {
    if (!this.daySelectContainer || !this.monthDaySelectContainer) return;

    const element = event.target;
    if (!(element instanceof HTMLSelectElement)) return;

    const { value } = element;
    if (value === RepeatType.Day) {
      this.daySelectContainer.hidden = true;
      this.monthDaySelectContainer.hidden = true;
      this.recurringHash.rule_type = RuleType.Daily;
      this.recurringHash.validations = {};
      this.recurringHash.week_start = undefined;
    } else if (value === RepeatType.Month) {
      this.daySelectContainer.hidden = true;
      this.monthDaySelectContainer.hidden = false;
      this.recurringHash.rule_type = RuleType.Monthly;
      this.recurringHash.week_start = 0;
      this.recurringHash.validations = {};
      this.syncMonthState();
    } else {
      this.daySelectContainer.hidden = false;
      this.monthDaySelectContainer.hidden = true;
      this.recurringHash.rule_type = RuleType.Weekly;
      this.recurringHash.week_start = 0;
      this.recurringHash.validations.day = this.recurringHash.validations.day || [];
      this.syncDayState();
    }
  }

  onRepeatFieldInput(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    const value = Number(target.value);
    const minValue = Number(target.getAttribute('min') || 1);
    // If value is less than the min value, show validation message before submitting the form.
    target.setCustomValidity(value >= minValue ? '' : `Value must be greater than or equal to ${minValue}`);
    this.recurringHash.interval = value;
  }

  onOccurrenceEndInput(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    this.recurringHash.count = Number(target.value);
  }

  onDayChange(event: CustomEvent) {
    const { relatedTarget, type } = event.detail;
    if (!(relatedTarget instanceof CheckboxOptionElement)) return;

    this.recurringHash.validations.day = this.recurringHash.validations.day || [];
    const dayValue = Number(relatedTarget.value);

    switch (type) {
      case CheckboxGroupState.Checked:
        if (this.recurringHash.validations.day.includes(dayValue)) return;
        this.recurringHash.validations.day.push(dayValue);
        break;
      case CheckboxGroupState.Unchecked:
        removeItem(this.recurringHash.validations.day, dayValue);
        break;
      default:
        log.warn(`Unhandled checkbox group state: ${type}`);
        break;
    }
  }

  onMonthChange() {
    this.recurringHash.validations = {};
    this.syncMonthState();
  }

  onDateChange(event: Event) {
    const datefield = event.target;
    if (!(datefield instanceof HTMLInputElement)) return;
    this.recurringHash.until = isoEndDateTime(datefield.value);
  }

  onEndChange(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || !this.occurrenceEndOnField || !this.occurrenceEndAfterField) return;

    switch (target.value) {
      case OccurrenceEndType.Never:
        this.recurringHash.until = undefined;
        this.recurringHash.count = undefined;
        this.enableOccurrenceEndType(target.value);
        break;
      case OccurrenceEndType.On:
        {
          this.recurringHash.count = undefined;
          const { value } = this.occurrenceEndOnField;
          this.recurringHash.until = isoEndDateTime(value);
          this.enableOccurrenceEndType(target.value);
        }
        break;
      case OccurrenceEndType.After:
        this.recurringHash.until = undefined;
        this.recurringHash.count = Number(this.occurrenceEndAfterField.value);
        this.enableOccurrenceEndType(target.value);
        break;
      default:
        log.warn(`Unhandled end change: ${target.value}`);
        break;
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();

    if (!this.repeatField?.reportValidity()) {
      return;
    }

    if (!this.occurrenceEndAfterField?.reportValidity()) {
      return;
    }

    const endRadio = this.endRadios.find((radio) => radio.checked);
    if (endRadio?.value === OccurrenceEndType.On && this.occurrenceEndOnField) {
      const untilValue = isoEndDateTime(this.occurrenceEndOnField.value);
      if (untilValue === undefined) {
        this.occurrenceEndOnField.setCustomValidity('Please select a valid date');
        this.occurrenceEndOnField.reportValidity();
        return;
      }
      this.occurrenceEndOnField.setCustomValidity('');
      this.recurringHash.until = untilValue;
    }

    this.element.value = JSON.stringify(this.recurringHash);
    this.element.dispatchEvent(new CustomEvent('recurring-select:commit'));
  }

  setDailyRule() {
    this.updateRepeatEveryField(RepeatType.Day);
    this.daySelectContainer?.setAttribute('hidden', '');
    this.monthDaySelectContainer?.setAttribute('hidden', '');
    this.updateEndFields();
  }

  setWeeklyRule() {
    this.updateRepeatEveryField(RepeatType.Week);
    this.daySelectContainer?.removeAttribute('hidden');
    this.monthDaySelectContainer?.setAttribute('hidden', '');
    this.updateDayFields();
    this.updateEndFields();
  }

  setMonthlyRule() {
    this.updateRepeatEveryField(RepeatType.Month);
    this.daySelectContainer?.setAttribute('hidden', '');
    this.monthDaySelectContainer?.removeAttribute('hidden');
    this.updateEndFields();
    this.updateMonthDayFields();
  }

  updateRepeatEveryField(value: string) {
    this.repeatFieldValue = this.recurringHash?.interval.toString();
    this.repeatTypeValue = value;
  }

  updateDayFields() {
    const { validations: { day } = {} } = this.recurringHash;
    if (!Array.isArray(day)) return;

    this.checkboxOptions.forEach((option) => {
      option.checked = day.includes(Number(option.value));
    });
  }

  updateMonthDayFields() {
    const keys = Object.keys(this.recurringHash.validations.day_of_week || {});
    const dayIndex = keys[0];
    if (typeof dayIndex === 'string' && this.monthDaySelect) {
      this.monthDaySelect.value = dayIndex;
    }
  }

  syncDayState() {
    this.checkboxOptions.forEach((option) => {
      const dayIndex = Number(option.value);
      if (option.checked && !this.recurringHash.validations.day?.includes(dayIndex)) {
        this.recurringHash.validations.day?.push(dayIndex);
      }
    });
  }

  syncMonthState() {
    if (this.monthDaySelect && this.monthOrdinalSelect) {
      this.recurringHash.validations.day_of_week = {
        [this.monthDaySelect.value]: [Number(this.monthOrdinalSelect.value)],
      };
    }
  }

  updateEndFields() {
    if (!this.recurringHash) return;
    const { until, count } = this.recurringHash;

    if (until === undefined) this.resetOccurrenceEndOnField();

    // Ends on case with a date
    if (until !== undefined) {
      this.endRadios.forEach(checkRadioWithValue(OccurrenceEndType.On));
      this.occurrenceEndOnField?.setAttribute('value', dayjs(until).format('YYYY-MM-DD'));
      this.enableOccurrenceEndType(OccurrenceEndType.On);
      return;
    }

    // Ends after case with occurrence count
    if (count !== undefined) {
      this.endRadios.forEach(checkRadioWithValue(OccurrenceEndType.After));
      if (this.occurrenceEndAfterField) this.occurrenceEndAfterField.value = count.toString();
      this.enableOccurrenceEndType(OccurrenceEndType.After);
      return;
    }

    // Never case
    this.endRadios.forEach(checkRadioWithValue(OccurrenceEndType.Never));
    this.enableOccurrenceEndType(OccurrenceEndType.Never);
  }

  enableOccurrenceEndType(type: OccurrenceEndType) {
    if (!this.occurrenceEndAfterField || !this.occurrenceEndOnField) return;

    switch (type) {
      case OccurrenceEndType.Never:
        this.occurrenceEndAfterField.disabled = true;
        this.occurrenceEndOnField.disabled = true;
        break;
      case OccurrenceEndType.On:
        this.occurrenceEndOnField.disabled = false;
        this.occurrenceEndAfterField.disabled = true;
        break;
      case OccurrenceEndType.After:
        this.occurrenceEndAfterField.disabled = false;
        this.occurrenceEndOnField.disabled = true;
        break;
      default:
        break;
    }
  }

  resetOccurrenceEndOnField() {
    if (!this.occurrenceEndOnField) return;
    const { value } = this.occurrenceEndOnField;
    const parsed = dayjs(value);
    this.occurrenceEndOnField.setAttribute(
      'value',
      parsed.isValid() ? parsed.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
    );
  }

  set repeatFieldValue(value: string | undefined) {
    if (!this.repeatField || !value) return;

    this.repeatField.value = value;
  }

  set repeatTypeValue(value: string | undefined) {
    if (!this.repeatType || !value) return;

    const options = Array.from(this.repeatType.options);
    options.forEach((option) => {
      option.selected = option.value === value;
    });
  }

  get defaultRecurringHash(): RecurringHash {
    const dayValues = this.checkboxOptions
      .map((option) => option.checked && Number(option.value))
      .filter(Boolean) as number[];

    return {
      rule_type: RuleType.Weekly,
      interval: Number(this.repeatField?.value),
      validations: {
        day: dayValues,
      },
    };
  }
}

function isoEndDateTime(date: Date | string): string | undefined {
  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return undefined;
  }
  return parsed.endOf('day').format();
}

function checkRadioWithValue(value: string) {
  return (target: HTMLInputElement) => {
    target.checked = target.value === value;
  };
}
